import type { FC, ReactNode } from 'react'

import { useEffect } from 'react'

const SELECTOR = '[data-reveal]:not([data-inview]), [data-chart]:not([data-inview])'

/**
 * Watches every element marked with data-reveal or data-chart and adds
 * data-inview the first time it enters the viewport. The CSS in
 * globals.css turns that attribute into the entrance animation.
 */
export const RevealObserver: FC = (): ReactNode => {
    useEffect(() => {
        const observer = new IntersectionObserver(entries => {
            for (const entry of entries) {
                if (!entry.isIntersecting) continue

                entry.target.setAttribute('data-inview', 'true')
                observer.unobserve(entry.target)
            }
        }, { rootMargin: '0px 0px -12% 0px', threshold: 0 })

        const observeWithin = (root: ParentNode) => {
            root.querySelectorAll(SELECTOR).forEach(element => observer.observe(element))
        }

        observeWithin(document)

        const mutations = new MutationObserver(records => {
            for (const record of records) {
                record.addedNodes.forEach(node => {
                    if (!(node instanceof Element)) return

                    if (node.matches(SELECTOR)) observer.observe(node)

                    observeWithin(node)
                })
            }
        })

        mutations.observe(document.body, { childList: true, subtree: true })

        return () => {
            observer.disconnect()
            mutations.disconnect()
        }
    }, [])

    return null
}
